import React, { useMemo } from 'react';
import { Input, Tag, Avatar, Spin } from 'antd';
import { SearchOutlined, HeartOutlined, MessageOutlined, FireOutlined } from '@ant-design/icons';
import { VirtuosoGrid } from 'react-virtuoso';
import useSWR from 'swr';
import { communityFetcher } from '../../api/community';
import { communityImages, handleImageError } from '../../utils/imageUtils';
import PageHeader from '../../components/PageHeader';
import EmptyState from '../../components/EmptyState';

/* —— 常量 —— */
const categories = ['全部', '活动策划', '海报设计', '品牌视觉', '文创产品设计', 'IP形象设计', 'Logo设计'];

/** 获取帖子图片URL */
const getPostImage = (title) => {
  return communityImages[title] || 'https://images.unsplash.com/photo-1558618666-fcd25c85f82e?w=400&h=300&fit=crop';
};

/* —— VirtuosoGrid 组件覆写（保持原有卡片样式 & 4 列 Grid 布局） —— */
const GridItem = React.forwardRef(({ children, ...props }, ref) => (
  <div {...props} ref={ref} className="virtuoso-grid-item" style={{ display: 'flex', padding: '10px 10px' }}>
    {children}
  </div>
));

const GridList = React.forwardRef(({ children, style, ...props }, ref) => (
  <div
    {...props}
    ref={ref}
    style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: 0,
      ...style,
    }}
  >
    {children}
  </div>
));

const ScrollSeekPlaceholder = () => (
  <div className="virtuoso-grid-item" style={{ display: 'flex', padding: '10px 10px' }}>
    <div className="glass-card" style={{ width: '100%', overflow: 'hidden' }}>
      <div style={{ aspectRatio: '4/3', background: 'var(--glass-sm)' }} />
      <div style={{ padding: 14, height: 80 }} />
    </div>
  </div>
);

/* —— 单个作品卡片 —— */
const PostCard = React.memo(({ post }) => (
  <div className="glass-card hover-lift" style={{ overflow: 'hidden', cursor: 'pointer', width: '100%' }}>
    {/* 图片区 */}
    <div style={{ position: 'relative', aspectRatio: '4/3', overflow: 'hidden' }}>
      <img
        src={getPostImage(post.title)}
        alt={post.title}
        onError={(e) => handleImageError(e)}
        loading="lazy"
        style={{
          width: '100%', height: '100%', objectFit: 'cover',
          transition: 'transform 300ms',
        }}
        onMouseOver={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
        onMouseOut={(e) => (e.currentTarget.style.transform = 'scale(1)')}
      />
      {/* 渐变遮罩 */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(180deg, transparent 60%, rgba(0,0,0,0.5) 100%)',
      }} />
      {post.featured && (
        <div
          className="animate-glow-pulse"
          style={{
            position: 'absolute', top: 8, right: 8,
            padding: '3px 8px', borderRadius: 6,
            background: 'rgba(180,210,160,0.85)',
            fontSize: 10, fontWeight: 600, color: '#fff',
          }}
        >
          精选
        </div>
      )}
    </div>
    {/* 信息 */}
    <div style={{ padding: '12px 14px 14px' }}>
      <div style={{
        fontSize: 14, fontWeight: 500, color: '#F0F5E8', marginBottom: 8,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {post.title}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 12, color: '#5C6B5C' }}>@{post.author}</span>
        <div style={{ display: 'flex', gap: 10, fontSize: 12, color: '#9CAF8C' }}>
          <span><HeartOutlined style={{ marginRight: 3 }} />{post.likes}</span>
          <span><MessageOutlined style={{ marginRight: 3 }} />{post.comments}</span>
        </div>
      </div>
      <div style={{ marginTop: 8 }}>
        <Tag className="tag-firefly">{post.type}</Tag>
      </div>
    </div>
  </div>
));

/* —— 加载骨架 —— */
const LoadingGrid = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '80px 0' }}>
    <Spin size="large" />
  </div>
);

/* —— 主页面组件 —— */
const CommunityPage = () => {
  const [activeCategory, setActiveCategory] = React.useState('全部');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [sortBy, setSortBy] = React.useState('featured');

  /* SWR 数据请求 — 替代 useState + useEffect */
  const { data: posts, isLoading, error, mutate } = useSWR(
    ['community-posts', activeCategory],
    () => communityFetcher({ category: activeCategory }),
    {
      revalidateOnFocus: false,
      dedupingInterval: 30000,
      fallbackData: [],
    },
  );

  /* 客户端筛选 & 排序 */
  const filtered = useMemo(() => {
    if (!posts) return [];
    let result = activeCategory === '全部'
      ? [...posts]
      : posts.filter((p) => p.type === activeCategory);

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter((p) =>
        p.title?.toLowerCase().includes(q) ||
        p.author?.toLowerCase().includes(q),
      );
    }

    return result.sort((a, b) => {
      if (sortBy === 'featured') return (b.featured ? 1 : 0) - (a.featured ? 1 : 0) || (b.likes ?? 0) - (a.likes ?? 0);
      return (b.likes ?? 0) - (a.likes ?? 0);
    });
  }, [posts, activeCategory, searchQuery, sortBy]);

  return (
    <div style={{ maxWidth: 1280, margin: '0 auto', padding: '28px 32px' }}>
      {/* PageHeader */}
      <PageHeader
        title="对话广场"
        description="发现社区优秀作品，一键复用 Prompt 和创意"
      />

      {/* 筛选 + 搜索 */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 22, flexWrap: 'wrap', gap: 14,
      }}>
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', flex: 1 }}>
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              style={{
                padding: '6px 14px', borderRadius: 20,
                background: activeCategory === cat ? 'rgba(180,210,160,0.08)' : 'rgba(255,255,255,0.03)',
                border: activeCategory === cat ? '1px solid rgba(180,210,160,0.2)' : '1px solid rgba(255,255,255,0.05)',
                color: activeCategory === cat ? '#B4D2A0' : '#5C6B5C',
                fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap',
                fontWeight: activeCategory === cat ? 500 : 400, transition: 'all 200ms',
              }}
            >
              {cat}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Input
            placeholder="搜索作品/作者..."
            prefix={<SearchOutlined style={{ color: '#5C6B5C' }} />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ width: 200, height: 36 }}
          />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)',
              borderRadius: 8, color: '#9CAF8C', fontSize: 12, padding: '0 10px', outline: 'none', cursor: 'pointer',
            }}
          >
            <option value="featured">精选</option>
            <option value="popular">最热</option>
          </select>
        </div>
      </div>

      {/* 错误状态 */}
      {error && !isLoading && (
        <EmptyState
          description="数据加载失败，请稍后重试"
          action={
            <button className="btn-firefly" onClick={() => mutate()} style={{ minWidth: 120, height: 38 }}>
              重试
            </button>
          }
        />
      )}

      {/* 加载中 */}
      {isLoading && !error && <LoadingGrid />}

      {/* 数据为空 */}
      {!isLoading && !error && filtered.length === 0 && (
        <EmptyState description={<span style={{ color: '#5C6B5C' }}>没有找到相关作品</span>} />
      )}

      {/* VirtuosoGrid 虚拟滚动作品列表 */}
      {!isLoading && !error && filtered.length > 0 && (
        <>
          <VirtuosoGrid
            data={filtered}
            itemContent={(index, post) => <PostCard post={post} />}
            style={{ height: 'calc(100vh - 230px)', width: '100%' }}
            components={{
              Item: GridItem,
              List: GridList,
              ScrollSeekPlaceholder,
            }}
            overscan={400}
          />

          {/* Load more */}
          <div style={{ textAlign: 'center', marginTop: 40 }}>
            <button className="btn-firefly" style={{ minWidth: 160, height: 42 }}>
              <FireOutlined style={{ marginRight: 6 }} />加载更多
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default CommunityPage;
